// Contact sheet (§11.17, D4): a PDF via pdf-lib, A4 or Letter; up to 6×8 = 48 photos per page (fewer and
// larger when there are fewer photos), then as many pages as needed. Each cell: the thumbnail (embedded
// JPEG, ~400 px), its number, the export file name and the star rating. Header: client, event, date, count.
// Footer: page numbers.
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export type SheetItem = { jpeg: Uint8Array; width: number; height: number; name: string; rating: number };
export type SheetOptions = {
  paper: 'a4' | 'letter';
  client?: string;
  event?: string;
  date?: string;
  /** Localized strings. */
  labels: { photos: (n: number) => string; page: (n: number, total: number) => string };
};

export const PER_PAGE = 48;
const PAPER = { a4: [595.28, 841.89], letter: [612, 792] } as const;

/** Grid for a page holding `n` photos: 6×8 when full, fewer and larger otherwise (keeps a 6:8 feel). */
export function gridFor(n: number): { cols: number; rows: number } {
  if (n >= PER_PAGE) return { cols: 6, rows: 8 };
  const cols = Math.max(1, Math.min(6, Math.ceil(Math.sqrt((n * 6) / 8))));
  return { cols, rows: Math.max(1, Math.ceil(n / cols)) };
}

/** Which photo numbers (1-based, export order) go on each page. */
export function sheetPages(count: number): number[][] {
  const pages: number[][] = [];
  for (let i = 0; i < count; i += PER_PAGE)
    pages.push(Array.from({ length: Math.min(PER_PAGE, count - i) }, (_, k) => i + k + 1));
  return pages.length ? pages : [[]];
}

/** WinAnsi-safe text for the standard fonts (accents survive; anything else becomes '?'). */
const safe = (s: string) => s.replace(/[^\x20-\x7e\xa0-\xff]/g, '?');

export async function buildContactSheet(items: SheetItem[], o: SheetOptions): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle(safe([o.client, o.event].filter(Boolean).join(' · ') || 'Contact sheet'));
  pdf.setProducer('EAT Photo Booth');
  pdf.setCreationDate(new Date(0)); // deterministic bytes
  pdf.setModificationDate(new Date(0));
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const [PW, PH] = PAPER[o.paper];
  const M = 28; // margin
  const headerH = 34,
    footerH = 20;
  const images = await Promise.all(items.map((it) => pdf.embedJpg(it.jpeg)));
  const pages = sheetPages(items.length);
  pages.forEach((nums, pi) => {
    const page = pdf.addPage([PW, PH]);
    // Header
    const title = [o.client, o.event].filter(Boolean).join(' · ');
    page.drawText(safe(title || ' '), { x: M, y: PH - M - 12, size: 12, font: bold });
    const right = safe([o.date, o.labels.photos(items.length)].filter(Boolean).join('   '));
    page.drawText(right, { x: PW - M - font.widthOfTextAtSize(right, 9), y: PH - M - 11, size: 9, font });
    // Footer
    const foot = safe(o.labels.page(pi + 1, pages.length));
    page.drawText(foot, { x: (PW - font.widthOfTextAtSize(foot, 8)) / 2, y: M - 12, size: 8, font });
    // Grid
    const { cols, rows } = gridFor(nums.length);
    const gw = PW - 2 * M,
      gh = PH - 2 * M - headerH - footerH;
    const cw = gw / cols,
      ch = gh / rows;
    const textH = 22;
    nums.forEach((num, k) => {
      const it = items[num - 1]!;
      const c = k % cols,
        r = Math.floor(k / cols);
      const x0 = M + c * cw,
        yTop = PH - M - headerH - r * ch;
      const boxW = cw - 8,
        boxH = ch - textH - 6;
      const s = Math.min(boxW / it.width, boxH / it.height);
      const w = it.width * s,
        h = it.height * s;
      page.drawImage(images[num - 1]!, {
        x: x0 + 4 + (boxW - w) / 2,
        y: yTop - 4 - h - (boxH - h) / 2,
        width: w,
        height: h,
      });
      const label = `${num}  ${safe(it.name)}`;
      const size = Math.max(5, Math.min(8, cw / 18));
      let text = label;
      if (font.widthOfTextAtSize(text, size) > cw - 8) {
        while (text.length > 4 && font.widthOfTextAtSize(text + '...', size) > cw - 8)
          text = text.slice(0, -1);
        text += '...';
      }
      page.drawText(text, { x: x0 + 4, y: yTop - ch + textH - 9, size, font });
      // Stars: filled for the rating, outlined for the rest (vector, so no font glyph is needed).
      const star = (cx: number, cy: number, rad: number, filled: boolean) => {
        const pts: string[] = [];
        for (let i = 0; i < 10; i++) {
          const a = -Math.PI / 2 + (i * Math.PI) / 5;
          const rr = i % 2 ? rad * 0.45 : rad;
          pts.push(`${(Math.cos(a) * rr).toFixed(2)} ${(Math.sin(a) * rr).toFixed(2)}`);
        }
        page.drawSvgPath(`M ${pts.join(' L ')} Z`, {
          x: cx,
          y: cy,
          color: filled ? rgb(0.95, 0.7, 0.1) : undefined,
          borderColor: rgb(0.5, 0.5, 0.5),
          borderWidth: 0.4,
        });
      };
      for (let i = 0; i < 5; i++) star(x0 + 8 + i * 9, yTop - ch + textH - 17, 3.5, i < it.rating);
    });
  });
  return pdf.save({ useObjectStreams: false });
}
